/* eslint-disable @typescript-eslint/no-explicit-any */
import { memo, useMemo, useState, useEffect } from 'react';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import { Item } from 'interface/project/projectInfo';
import { Stack } from '@mui/system';
import { useProject } from 'hooks/useProject';
import { Box } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';

interface ISurveyLKProps {
  id: string;
  items: Item[];
  type: string;
  handleNext: (_type: string, _surveyId: string, _info: any) => void;
  setIsDisabled: React.Dispatch<React.SetStateAction<boolean>>;
  surveyType: 'survey' | 'surveyAdd';
  handleNextButton?: () => void;
}

function SurveyLK({
  id,
  items,
  type,
  handleNext,
  setIsDisabled,
  surveyType,
  handleNextButton,
}: ISurveyLKProps): JSX.Element {
  const { progressInfo, surveyResultList, surveyInfoResultList } = useProject();
  const resultList = surveyType === 'surveyAdd' ? surveyResultList : surveyInfoResultList;
  const buttonInfo = useMemo(() => {
    return resultList?.[progressInfo]?.choiceResultList || [''];
  }, [resultList, progressInfo]);
  const [value, setValue] = useState('');
  const isMobile = useMediaQuery('(max-width:600px)');
  useEffect(() => {
    setValue(buttonInfo?.[0] || '');
  }, [buttonInfo]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const tmpValue = (event.target as HTMLInputElement).value;
    if (tmpValue) {
      setValue(tmpValue);
      setTimeout(() => {
        handleNext(type, id, tmpValue);
        if (handleNextButton) {
          setTimeout(() => {
            handleNextButton();
          }, 300);
        }
      }, 200);
    }
  };

  useEffect(() => {
    setIsDisabled(value === '');
  }, [setIsDisabled, value]);

  // 항목 분할 로직
  const isTwoRows = items.length > 5;
  let upperItems: Item[] = [];
  let lowerItems: Item[] = [];
  if (isTwoRows) {
    const half = Math.ceil(items.length / 2);
    upperItems = items.slice(0, half);
    lowerItems = items.slice(half);
  }

  return (
    <Stack direction="column">
      {!isTwoRows ? (
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={isMobile ? 0.9 : 1}>
          <Box
            sx={{
              flex: isMobile ? 0.7 : 1,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'flex-start',
              minHeight: 60,
            }}
          >
            <span style={{ fontSize: 14 }}>매우 그렇지 않다</span>
          </Box>
          <FormControl component="fieldset">
            <RadioGroup row value={value} onChange={handleChange} defaultValue="">
              {items.map(item => (
                <Stack key={item.id} direction="column" justifyContent="center" alignItems="center">
                  <Box sx={{ height: 20 }}>{item.content}</Box>
                  <FormControlLabel value={item.id} label="" control={<Radio />} sx={{ mx: 0 }} />
                </Stack>
              ))}
            </RadioGroup>
          </FormControl>
          <Box
            sx={{
              flex: isMobile ? 0.7 : 1,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
          >
            <span style={{ fontSize: 14 }}>
              {isMobile ? (
                <>
                  &nbsp;&nbsp;매우
                  <br />
                  그렇다
                </>
              ) : (
                '매우 그렇다'
              )}
            </span>
          </Box>
        </Stack>
      ) : (
        <Stack direction="row" alignItems="stretch" sx={{ width: '100%' }}>
          {/* 왼쪽 영역 */}
          <Box
            sx={{
              flex: isMobile ? 0.7 : 1,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-start',
              alignItems: 'center',
              marginTop: '20px',
            }}
          >
            <span style={{ fontSize: 14 }}>매우 그렇지 않다</span>
          </Box>
          {/* 가운데 영역 (2) */}
          <Box sx={{ flex: isMobile ? 2.8 : 2 }}>
            {/* 위줄 */}
            <Stack direction="row" justifyContent="center" alignItems="flex-end" spacing={isMobile ? 0.9 : 1}>
              <FormControl component="fieldset">
                <RadioGroup row value={value} onChange={handleChange} defaultValue="">
                  {upperItems.map(item => (
                    <Stack key={item.id} direction="column" justifyContent="center" alignItems="center">
                      <Box sx={{ height: 20 }}>{item.content}</Box>
                      <FormControlLabel value={item.id} label="" control={<Radio />} sx={{ mx: 0 }} />
                    </Stack>
                  ))}
                </RadioGroup>
              </FormControl>
            </Stack>
            {/* 아래줄 */}
            <Stack direction="row" justifyContent="center" alignItems="flex-start" spacing={isMobile ? 0.9 : 1} mt={1}>
              <FormControl component="fieldset">
                <RadioGroup row value={value} onChange={handleChange} defaultValue="">
                  {lowerItems.map(item => (
                    <Stack key={item.id} direction="column" justifyContent="center" alignItems="center">
                      <Box sx={{ height: 20 }}>{item.content}</Box>
                      <FormControlLabel value={item.id} label="" control={<Radio />} sx={{ mx: 0 }} />
                    </Stack>
                  ))}
                </RadioGroup>
              </FormControl>
            </Stack>
          </Box>
          {/* 오른쪽 영역 */}
          <Box
            sx={{
              flex: isMobile ? 0.7 : 1,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-end',
              alignItems: 'center',
              marginBottom: '20px',
            }}
          >
            <span style={{ fontSize: 14 }}>
              {isMobile ? (
                <>
                  &nbsp;&nbsp;매우
                  <br />
                  그렇다
                </>
              ) : (
                '매우 그렇다'
              )}
            </span>
          </Box>
        </Stack>
      )}
    </Stack>
  );
}

export default memo(SurveyLK);
